import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export default function BountyBoard() {
  const navigate = useNavigate();
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // New Bounty State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Electronics & Computers');
  const [reward, setReward] = useState('');
  const [location, setLocation] = useState('');
  const [urgency, setUrgency] = useState('Flexible');
  const [condition, setCondition] = useState('Any Condition');
  
  // Board Filter State
  const [filterLocation, setFilterLocation] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const categories = ['Electronics & Computers', 'Apparel & Accessories', 'Home & Furniture', 'Automotive', 'Toys & Hobbies', 'Other'];
  const urgencies = ['ASAP', 'This Week', 'Flexible'];
  const conditions = ['Any Condition', 'New Only', 'Like New', 'Good or Better', 'Fair/Acceptable'];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'bounties'), where('status', '==', 'active'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activeBounties = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort newest first
      activeBounties.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
      setBounties(activeBounties);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handlePostBounty = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) return alert("Please log in to post an ISO request.");
    if (!title || !reward || !location) return alert("Title, location, and reward amount are required.");

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'bounties'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        title,
        description,
        category,
        reward: Number(reward),
        location,
        urgency,
        condition,
        status: 'active',
        createdAt: serverTimestamp()
      });
      
      setShowForm(false);
      setTitle('');
      setDescription('');
      setReward('');
      setLocation('');
      setUrgency('Flexible');
      setCondition('Any Condition');
      alert("Bounty posted! The area will be on the lookout.");
    } catch (error) {
      console.error("Error posting bounty:", error);
      alert("Failed to post bounty.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBounty = async (id) => {
    if (window.confirm("Remove this bounty?")) {
      try {
        await deleteDoc(doc(db, 'bounties', id));
      } catch (error) {
        alert("Failed to delete bounty.");
      }
    }
  };

  const handleFulfillBounty = async (bounty) => {
    if (!auth.currentUser) return alert("Please log in to respond to this request.");
    if (auth.currentUser.uid === bounty.userId) return alert("You cannot fulfill your own bounty!");

    try {
      const q = query(collection(db, 'chats'), where('bountyId', '==', bounty.id), where('participants', 'array-contains', auth.currentUser.uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await addDoc(collection(db, 'chats'), {
          bountyId: bounty.id,
          itemTitle: `ISO: ${bounty.title}`,
          participants: [auth.currentUser.uid, bounty.userId],
          buyerId: bounty.userId, // The poster is the buyer
          updatedAt: serverTimestamp(),
          lastMessage: ""
        });
      }

      sessionStorage.setItem('pendingOffer', `👋 I have the hardware you are looking for: ${bounty.title}. Let's make a deal!`);
      navigate('/inbox');
      
    } catch (error) {
      console.error("Error starting chat:", error);
      alert("Could not start conversation.");
    }
  };

  const activeCities = [...new Set(bounties.map(b => b.location).filter(Boolean))].sort();

  const displayedBounties = bounties.filter(b => {
    if (!filterLocation) return true;
    return b.location === filterLocation; 
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#0f172a', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '18px' }}>Scanning Global Boards...</h2>
          <p style={{ color: '#64748b' }}>Locating local bounties.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: isMobile ? '20px 10px' : '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Elegant Hero Banner */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'white', padding: isMobile ? '30px 20px' : '50px 60px', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', marginBottom: '30px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '24px' }}>
          <div>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎯</div>
            <h1 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '32px' : '44px', fontWeight: '800', letterSpacing: '-1px' }}>Global ISO Board</h1>
            <p style={{ margin: 0, fontSize: '16px', color: '#94a3b8', maxWidth: '500px', lineHeight: '1.5' }}>In Search Of: Post a bounty for specific gear, or fulfill requests in your city for instant cash.</p>
          </div>
          <button 
            onClick={() => setShowForm(!showForm)} 
            style={{ padding: '16px 36px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '50px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)', transition: 'all 0.2s ease', width: isMobile ? '100%' : 'auto' }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {showForm ? 'Cancel Request' : '+ Post a Bounty'}
          </button>
        </div>

        {/* Create Bounty Form */}
        {showForm && (
          <div style={{ backgroundColor: 'white', padding: isMobile ? '30px 20px' : '40px', borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)', marginBottom: '40px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.3s ease' }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '22px', color: '#0f172a', fontWeight: '800' }}>What gear are you hunting for?</h3>
            <form onSubmit={handlePostBounty} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                  <label style={labelStyle}>Specific Item / Hardware</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Dell Latitude 3520 Replacement Keyboard" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} autoFocus />
                </div>
                <div>
                  <label style={labelStyle}>Willing to Pay ($)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: '700' }}>$</span>
                    <input type="number" value={reward} onChange={(e) => setReward(e.target.value)} placeholder="0.00" required style={{ ...inputStyle, paddingLeft: '32px' }} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Your City / Location</label>
                  <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g., Chicago, IL" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Urgency</label>
                  <select value={urgency} onChange={(e) => setUrgency(e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                    {urgencies.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Condition Required</label>
                  <select value={condition} onChange={(e) => setCondition(e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                    {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Details (Specs needed, drop-off info, etc.)</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Must be in working condition, don't mind a few scratches..." rows="3" style={{ ...inputStyle, resize: 'vertical' }} onFocus={handleFocus} onBlur={handleBlur} />
              </div>

              <button type="submit" disabled={submitting} style={{ padding: '16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer', marginTop: '10px', transition: 'background-color 0.2s' }}
                 onMouseOver={e => !submitting && (e.currentTarget.style.backgroundColor = '#4338ca')}
                 onMouseOut={e => !submitting && (e.currentTarget.style.backgroundColor = '#4f46e5')}
              >
                {submitting ? 'Broadcasting...' : 'Broadcast to City'}
              </button>
            </form>
          </div>
        )}

        {/* Dynamic City Dropdown */}
        {!showForm && bounties.length > 0 && (
          <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '16px', backgroundColor: 'white', padding: '16px 24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '24px' }}>📍</span>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Filter Bounties by City</label>
              <select 
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: '16px', color: '#0f172a', fontWeight: '600', backgroundColor: 'transparent', cursor: 'pointer' }}
              >
                <option value="">Global View (All Cities)</option>
                {activeCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            {filterLocation && (
              <button onClick={() => setFilterLocation('')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '700', fontSize: '14px', padding: '8px 16px', borderRadius: '8px', backgroundColor: '#fef2f2' }}>Clear</button>
            )}
          </div>
        )}

        {/* Bounty Feed */}
        {displayedBounties.length === 0 ? (
          <div style={{ padding: '80px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '24px', border: '1px dashed #cbd5e1' }}>
            <div style={{ fontSize: '48px', opacity: 0.5, marginBottom: '16px' }}>{filterLocation ? '🏙️' : '🏜️'}</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '20px', fontWeight: '800' }}>No Bounties Found</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>{filterLocation ? `No one in ${filterLocation} has posted an ISO request yet.` : 'The board is clear. Be the first to post a bounty!'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '340px'}, 1fr))`, gap: '24px' }}>
            {displayedBounties.map(bounty => (
              <div key={bounty.id} style={{ backgroundColor: 'white', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)'; }}
              >
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ fontSize: '11px', backgroundColor: '#e0e7ff', color: '#4f46e5', padding: '4px 10px', borderRadius: '6px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {bounty.category}
                  </span>
                  {bounty.location && (
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      📍 {bounty.location}
                    </span>
                  )}
                </div>

                <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#0f172a', lineHeight: '1.4', fontWeight: '700' }}>{bounty.title}</h3>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: bounty.urgency === 'ASAP' ? '#fef3c7' : '#f1f5f9', color: bounty.urgency === 'ASAP' ? '#d97706' : '#475569', borderRadius: '6px', fontWeight: '700', border: bounty.urgency === 'ASAP' ? '1px solid #fde68a' : '1px solid #e2e8f0' }}>
                    ⏳ {bounty.urgency || 'Flexible'}
                  </span>
                  <span style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '6px', fontWeight: '700', border: '1px solid #e2e8f0' }}>
                    📦 {bounty.condition || 'Any Condition'}
                  </span>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', flexGrow: 1 }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                    {bounty.description || "No specific details provided."}
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bounty Offer</p>
                    <p style={{ margin: 0, fontSize: '28px', fontWeight: '900', color: '#059669', letterSpacing: '-0.5px' }}>${bounty.reward.toFixed(2)}</p>
                  </div>
                  
                  {auth.currentUser?.uid === bounty.userId ? (
                    <button onClick={() => handleDeleteBounty(bounty.id)} style={{ padding: '10px 20px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', transition: 'background-color 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      Remove
                    </button>
                  ) : (
                    <button onClick={() => handleFulfillBounty(bounty)} style={{ padding: '12px 24px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'background-color 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.backgroundColor = '#1e293b'}
                      onMouseOut={e => e.currentTarget.style.backgroundColor = '#0f172a'}
                    >
                      I Have This!
                    </button>
                  )}
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Styles
const labelStyle = { 
  display: 'block', 
  marginBottom: '8px', 
  fontWeight: '700', 
  fontSize: '13px', 
  color: '#0f172a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const inputStyle = { 
  width: '100%', 
  padding: '14px 16px', 
  borderRadius: '12px', 
  border: '1px solid #cbd5e1', 
  backgroundColor: '#f8fafc', 
  fontSize: '15px', 
  color: '#0f172a', 
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'all 0.2s ease'
};

const handleFocus = (e) => {
  e.target.style.backgroundColor = 'white';
  e.target.style.borderColor = '#4f46e5';
  e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
};

const handleBlur = (e) => {
  e.target.style.backgroundColor = '#f8fafc';
  e.target.style.borderColor = '#cbd5e1';
  e.target.style.boxShadow = 'none';
};